import React, { PropsWithChildren, useMemo } from 'react';
import { Field, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { ClaimCloseoutType } from '../../../api';
import { FormField } from '../../form-field';
import { SelectField } from '../../base/form';
import { BaseButton, BaseCheckbox, ISelectOption, Variables, getOptions } from '../../base';
import { VOID_CLAIM_HELPER } from './closeout-void';

export interface CloseoutFormValues {
    closeoutType: ClaimCloseoutType;
    closeoutDescription?: string;
    closeoutVoid?: boolean;
}

// Shared helper to normalize a stored string into a valid select value
// by matching either option.value or option.label.
export const normalizeSelectValue = (
    raw: string | null | undefined,
    options: ISelectOption[],
): string | undefined => {
    if (!raw) {
        return undefined;
    }

    const byValue = options.find(opt => opt.value === raw);
    if (byValue) {
        return byValue.value as string;
    }

    const byLabel = options.find(opt => opt.label === raw);
    return byLabel ? (byLabel.value as string) : undefined;
};

export interface CloseoutFormProps {
    initialValues: CloseoutFormValues;
    /** Options for the closeout description select (typically built from a ClaimCloseoutDescription enum). */
    descriptionOptions: ISelectOption[];
    onSubmit: (
        values: CloseoutFormValues,
        formikHelpers?: FormikHelpers<CloseoutFormValues>,
    ) => Promise<void> | void;
    onCancel: () => void;
    /** When true, the claim is already voided: type stays CLOSED and void cannot be cleared. */
    voidLocked?: boolean;
    /** Optional wrapper components for styling (e.g. styled.section) */
    FormContainerComponent?: React.ComponentType<PropsWithChildren<unknown>>;
    ContainerComponent?: React.ComponentType<PropsWithChildren<unknown>>;
}

const CloseoutSchema = Yup.object().shape({
    closeoutType: Yup.mixed<ClaimCloseoutType>()
        .required('Closeout type is required')
        .test(
            'void-requires-closed',
            'A claim can only be voided if it is also closed',
            function (value) {
                if (this.parent.closeoutVoid && value !== ClaimCloseoutType.CLOSED) {
                    return false;
                }
                return true;
            },
        ),
    closeoutVoid: Yup.boolean(),
});

export const CloseoutForm: React.FC<CloseoutFormProps> = ({
    initialValues,
    descriptionOptions,
    onSubmit,
    onCancel,
    voidLocked = false,
    FormContainerComponent,
    ContainerComponent,
}) => {
    const Wrapper = (FormContainerComponent ?? React.Fragment) as React.ComponentType<PropsWithChildren<unknown>>;
    const Inner = (ContainerComponent ?? React.Fragment) as React.ComponentType<PropsWithChildren<unknown>>;

    const typeOptions = useMemo<ISelectOption[]>(
        () => getOptions(ClaimCloseoutType),
        [],
    );

    return (
        <Wrapper>
            <Formik<CloseoutFormValues>
                initialValues={initialValues}
                validationSchema={CloseoutSchema}
                onSubmit={onSubmit}
            >
                {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
                    <Inner>
                        <form
                            onSubmit={handleSubmit}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16,
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: 22,
                                paddingTop: 8,
                                margin: 0,
                            }}
                        >
                            <FormField label="Closeout Type" required>
                                <Field
                                    name="closeoutType"
                                    component={SelectField}
                                    options={typeOptions}
                                    disabled={voidLocked}
                                    onChange={(option: ISelectOption | null) => {
                                        if (voidLocked) {
                                            return;
                                        }
                                        if (option?.value !== ClaimCloseoutType.CLOSED) {
                                            setFieldValue('closeoutVoid', false);
                                        }
                                    }}
                                />
                            </FormField>

                            <FormField label="Closeout Description">
                                <Field name="closeoutDescription" component={SelectField} options={descriptionOptions} />
                            </FormField>

                            {values.closeoutType === ClaimCloseoutType.CLOSED && (
                                <FormField>
                                    <BaseCheckbox
                                        name="closeoutVoid"
                                        checked={!!values.closeoutVoid}
                                        disabled={voidLocked}
                                        onChange={e => {
                                            if (voidLocked) {
                                                return;
                                            }
                                            setFieldValue('closeoutVoid', e.target.checked);
                                        }}
                                        label="Void this claim"
                                    />
                                    <span
                                        style={{
                                            display: 'block',
                                            marginTop: 8,
                                            fontSize: 12,
                                            color: Variables.ThemeLight.TextMild,
                                        }}
                                    >
                                        {VOID_CLAIM_HELPER}
                                    </span>
                                </FormField>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <BaseButton type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
                                    Cancel
                                </BaseButton>
                                <BaseButton type="submit" variant="primary" disabled={isSubmitting}>
                                    Submit
                                </BaseButton>
                            </div>
                        </form>
                    </Inner>
                )}
            </Formik>
        </Wrapper>
    );
};
